import { useEffect, useRef } from 'react';
import { debug } from '@/lib/debug';

/**
 * Enterprise-grade version check  -  inspired by how Notion, Linear, and Slack handle updates.
 * 
 * KEY PRINCIPLES:
 * 1. NEVER force-reload while the user is actively working
 * 2. Only reload on "first access of the day" (after 8+ hours idle)
 * 3. For mid-session deploys, show a gentle banner (handled by PWAUpdateBanner)
 * 4. Handle stale chunk errors gracefully (handled by lazyWithRetry in AnimatedRoutes)
 * 
 * WHAT THIS DOES:
 * - On mount: checks if the app version changed since last visit
 * - If version changed AND session is stale (8+ hours): reload once to get fresh code
 * - If version changed BUT session is recent: just update the stored version (no reload)
 *   The user will get the new version on their next natural page load
 * - If version is the same: do nothing
 * 
 * WHAT THIS DOES NOT DO:
 * - Force-reload on every tab switch
 * - Nuke all caches aggressively
 * - Unregister service workers (that's the PWA system's job)
 */

const BUILD_VERSION = (import.meta as any).env.VITE_BUILD_TIMESTAMP || 'dev';
const VERSION_KEY = 'vb-app-version';
const LAST_ACTIVE_KEY = 'vb-last-active-timestamp';
const VERSION_RELOAD_FLAG = 'vb-version-reload-done';
const STALE_THRESHOLD_MS = 8 * 60 * 60 * 1000; // 8 hours

export function useVersionCheck() {
  const hasChecked = useRef(false);

  useEffect(() => {
    // Only check once per mount
    if (hasChecked.current) return;
    hasChecked.current = true;

    if (BUILD_VERSION === 'dev') return;

    const storedVersion = localStorage.getItem(VERSION_KEY);
    const lastActive = localStorage.getItem(LAST_ACTIVE_KEY);
    const alreadyReloaded = sessionStorage.getItem(VERSION_RELOAD_FLAG);

    // First visit ever  -  just store the version
    if (!storedVersion) {
      localStorage.setItem(VERSION_KEY, BUILD_VERSION);
      localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
      return;
    }

    // Same version  -  nothing to do
    if (storedVersion === BUILD_VERSION) {
      localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
      return;
    }

    // Version changed! But should we reload?
    const elapsed = lastActive ? Date.now() - parseInt(lastActive, 10) : Infinity;
    const isStaleSession = elapsed > STALE_THRESHOLD_MS;

    if (isStaleSession && !alreadyReloaded) {
      // First access of the day with a new version  -  reload to get fresh code
      debug.log(
        `[VersionCheck] New version detected after ${Math.round(elapsed / 3600000)}h idle. ` +
        `Updating: ${storedVersion} → ${BUILD_VERSION}`
      );

      // Set flag to prevent infinite reload loop
      sessionStorage.setItem(VERSION_RELOAD_FLAG, 'true');
      localStorage.setItem(VERSION_KEY, BUILD_VERSION);
      localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());

      // Gentle reload  -  clear only stale caches, don't nuke everything
      if ('caches' in window) {
        caches.keys().then(keys => {
          // Only clear workbox precache, keep runtime caches (fonts, images)
          const staleCaches = keys.filter(k => k.includes('precache') || k.includes('html-pages'));
          Promise.all(staleCaches.map(k => caches.delete(k))).then(() => {
            window.location.reload();
          });
        });
      } else {
        (window as any).location.reload();
      }
    } else {
      // Mid-session version change  -  just update the stored version
      // The user will get the new code on their next natural page load
      // or when the PWAUpdateBanner prompts them
      debug.log(
        `[VersionCheck] New version available (${storedVersion} → ${BUILD_VERSION}), ` +
        `but session is active (${Math.round(elapsed / 60000)}min). Deferring update.`
      );
      localStorage.setItem(VERSION_KEY, BUILD_VERSION);
      localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());

      // Clear the reload flag for next time
      sessionStorage.removeItem(VERSION_RELOAD_FLAG);
    }
  }, []);
}
